import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getSchoolNetwork,
  deleteSchoolNetwork,
  getNetworkMetrics,
  updateSchoolNetwork,
  updateNetworkSchools,
  getAvailableSchools,
  uploadNetworkFile,
} from '@/api/school-networks';
import { pushWhiteLabelBranding } from '@/api/white-labels';
import type { SchoolNetworkDetail, NetworkMetrics, AvailableSchool } from '@/types/school-network';
import type { MultiSelectOption } from '@/components/ui/multi-select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MultiSelect } from '@/components/ui/multi-select';
import { FileUpload } from '@/components/ui/file-upload';
import { AttachSchoolDialog } from '@/components/school-networks/AttachSchoolDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Network,
  Trash2,
  School,
  ExternalLink,
  Users,
  GraduationCap,
  BookOpen,
  Activity,
  Mail,
  UserCheck,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  Server,
  Globe,
  Plus,
  Upload,
  Info,
  Image as ImageIcon,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEditableCard } from '@/hooks/useEditableCard';
import { EditableCardActions } from '@/components/schools/detail/EditableCardActions';

function getStatusBadgeVariant(
  status: string
): 'success' | 'warning' | 'default' | 'destructive' {
  switch (status) {
    case 'production':
      return 'success';
    case 'demo_empty':
    case 'demo_datas':
      return 'warning';
    case 'cancelled':
      return 'destructive';
    default:
      return 'default';
  }
}

function getV2StatusBadge(status: string | null) {
  switch (status) {
    case 'ACTIVE':
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Actif</Badge>;
    case 'INACTIVE':
      return <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">Inactif</Badge>;
    case 'SUSPENDED':
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Suspendu</Badge>;
    default:
      return <Badge variant="outline">-</Badge>;
  }
}

type SortField = 'name' | 'status' | 'studentCount' | 'parentCount' | 'teacherCount' | 'classCount' | 'connexionCount' | 'mailCount';
type SortDirection = 'asc' | 'desc';

interface InfoFormData {
  [key: string]: unknown;
  name: string;
  description: string;
  host: string;
  senderEmail: string;
  senderName: string;
}

export function SchoolNetworkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [network, setNetwork] = useState<SchoolNetworkDetail | null>(null);
  const [metrics, setMetrics] = useState<NetworkMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [pushingBranding, setPushingBranding] = useState(false);
  const [brandingMessage, setBrandingMessage] = useState<string | null>(null);
  const [uploadingField, setUploadingField] = useState<string | null>(null);

  // Attach dialog (réseaux physiques)
  const [attachOpen, setAttachOpen] = useState(false);

  // Schools editing state (réseaux virtuels)
  const [schoolsEditing, setSchoolsEditing] = useState(false);
  const [schoolsSaving, setSchoolsSaving] = useState(false);
  const [schoolsError, setSchoolsError] = useState<string | null>(null);
  const [availableSchools, setAvailableSchools] = useState<AvailableSchool[]>([]);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  const canDelete = hasPermission('action.school_networks.delete');
  const canEdit = hasPermission('action.school_networks.edit');
  const canAttach = hasPermission('action.school_networks.attach_school');

  const isPhysical = network?.type === 'physical';

  const fetchNetwork = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSchoolNetwork(parseInt(id!));
      setNetwork(data);
    } catch (err) {
      setError('Erreur lors du chargement du reseau');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchMetrics = useCallback(async () => {
    try {
      setMetricsLoading(true);
      const data = await getNetworkMetrics(parseInt(id!));
      setMetrics(data);
    } catch (err) {
      console.error('Erreur lors du chargement des metriques:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchNetwork();
      fetchMetrics();
    }
  }, [id, fetchNetwork, fetchMetrics]);

  // --- Info card editable ---
  const infoInitialData: InfoFormData = useMemo(() => ({
    name: network?.name || '',
    description: network?.description || '',
    host: network?.v2Host || '',
    senderEmail: network?.senderEmail || '',
    senderName: network?.senderName || '',
  }), [network?.name, network?.description, network?.v2Host, network?.senderEmail, network?.senderName]);

  const infoCard = useEditableCard<InfoFormData>({
    initialData: infoInitialData,
    onSave: async (data) => {
      await updateSchoolNetwork(parseInt(id!), {
        name: data.name.trim(),
        description: data.description.trim() || null,
        host:
          isPhysical && data.host.trim() && data.host.trim() !== network?.v2Host
            ? data.host.trim()
            : undefined,
        senderEmail: isPhysical ? data.senderEmail.trim() || null : undefined,
        senderName: isPhysical ? data.senderName.trim() || null : undefined,
      });
    },
    onSaved: () => fetchNetwork(),
  });

  // --- Push branding (réseau physique) ---
  const handlePushBranding = async () => {
    if (!network?.whiteLabel) return;
    try {
      setPushingBranding(true);
      setBrandingMessage(null);
      const result = await pushWhiteLabelBranding(network.whiteLabel.id);
      setBrandingMessage(result.message || (result.pushed ? 'Branding poussé' : result.reason || null));
    } catch (err: any) {
      setBrandingMessage(err?.response?.data?.error || 'Erreur lors du push du branding');
    } finally {
      setPushingBranding(false);
    }
  };

  // --- Upload d'une image de branding V2 portée par le réseau ---
  const handleUploadNetworkFile = async (
    field: 'logoSignin' | 'favicon' | 'cover',
    file: File
  ) => {
    try {
      setUploadingField(field);
      setBrandingMessage(null);
      const result = await uploadNetworkFile(parseInt(id!), field, file);
      await fetchNetwork();
      setBrandingMessage(
        result.pushWarning
          ? `Image enregistrée mais non poussée vers V2 : ${result.pushWarning}`
          : result.pushed
            ? 'Image enregistrée et poussée vers la V2'
            : 'Image enregistrée'
      );
    } catch (err: any) {
      setBrandingMessage(err?.response?.data?.error || "Erreur lors de l'upload de l'image");
    } finally {
      setUploadingField(null);
    }
  };

  // --- Schools card editable (virtuels) ---
  const schoolOptions: MultiSelectOption[] = useMemo(
    () =>
      availableSchools.map((school) => ({
        value: String(school.id),
        label: school.name,
        description: `(${school.code})`,
      })),
    [availableSchools]
  );

  const selectedSchoolValues = useMemo(
    () => selectedSchoolIds.map(String),
    [selectedSchoolIds]
  );

  const handleSchoolsStartEditing = async () => {
    setSchoolsEditing(true);
    setSchoolsError(null);
    try {
      setSchoolsLoading(true);
      const data = await getAvailableSchools(parseInt(id!));
      setAvailableSchools(data);
      setSelectedSchoolIds(data.filter((s) => s.inNetwork).map((s) => s.id));
    } catch (err) {
      console.error(err);
      setSchoolsError('Erreur lors du chargement des écoles disponibles');
    } finally {
      setSchoolsLoading(false);
    }
  };

  const handleSchoolsSave = async () => {
    try {
      setSchoolsSaving(true);
      setSchoolsError(null);
      await updateNetworkSchools(parseInt(id!), selectedSchoolIds);
      setSchoolsEditing(false);
      fetchNetwork();
      fetchMetrics();
    } catch (err: any) {
      setSchoolsError(
        err?.response?.data?.error ||
          (err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
      );
    } finally {
      setSchoolsSaving(false);
    }
  };

  // --- Sorting & delete ---
  const handleDelete = async () => {
    if (!network) return;
    try {
      setDeleting(true);
      await deleteSchoolNetwork(network.id);
      navigate('/school-networks');
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error || 'Erreur lors de la suppression du reseau');
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSchools = useMemo(() => {
    if (!metrics?.schools) return [];

    return [...metrics.schools].sort((a, b) => {
      let comparison = 0;

      if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === 'status') {
        comparison = a.status.localeCompare(b.status);
      } else {
        const aValue = a.metrics?.[sortField] ?? 0;
        const bValue = b.metrics?.[sortField] ?? 0;
        comparison = aValue - bValue;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [metrics?.schools, sortField, sortDirection]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 ml-1" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1" />;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!network) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Reseau non trouve</p>
        <Button asChild className="mt-4">
          <Link to="/school-networks">Retour aux reseaux</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to="/school-networks">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {isPhysical ? <Server className="h-5 w-5" /> : <Network className="h-5 w-5" />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                  {network.name}
                </h1>
                {isPhysical ? (
                  <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 gap-1">
                    <Server className="h-3 w-3" />
                    Physique
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1">
                    <Network className="h-3 w-3" />
                    Virtuel
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {network.schoolCount} école{network.schoolCount !== 1 ? 's' : ''}
                {isPhysical && network.v2Host && (
                  <span className="font-mono"> · {network.v2Host}</span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {canDelete && !isPhysical && (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* KPIs globaux */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                <GraduationCap className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.studentCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Élèves</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.parentCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Parents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                <UserCheck className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.teacherCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Enseignants</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.classCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Classes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100 text-cyan-600">
                <Activity className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.connexionCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Connexions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-pink-100 text-pink-600">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.mailCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Mails envoyes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Network info */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Informations - 1/3 */}
        <Card>
          <CardHeader
            actions={
              canEdit ? (
                <EditableCardActions
                  isEditing={infoCard.isEditing}
                  isSaving={infoCard.isSaving}
                  onEdit={infoCard.startEditing}
                  onSave={infoCard.save}
                  onCancel={infoCard.cancelEditing}
                />
              ) : undefined
            }
          >
            <CardTitle className="text-base">Informations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {infoCard.error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-700 text-sm">
                {infoCard.error}
              </div>
            )}

            {infoCard.isEditing ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="info-name">
                    Nom <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="info-name"
                    value={infoCard.formData.name}
                    onChange={(e) => infoCard.updateField('name', e.target.value)}
                    placeholder="Ex: Reseau Paris Est"
                    required
                  />
                </div>
                {isPhysical && (
                  <div className="space-y-2">
                    <Label htmlFor="info-host" className="flex items-center gap-1">
                      <Globe className="h-3.5 w-3.5" />
                      Domaine (host)
                    </Label>
                    <Input
                      id="info-host"
                      value={infoCard.formData.host}
                      onChange={(e) => infoCard.updateField('host', e.target.value)}
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">
                      Modifie le host du group tenant V2.
                    </p>
                  </div>
                )}
                {isPhysical && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="info-sender-email" className="flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5" />
                        Email d'expédition
                      </Label>
                      <Input
                        id="info-sender-email"
                        type="email"
                        value={infoCard.formData.senderEmail}
                        onChange={(e) => infoCard.updateField('senderEmail', e.target.value)}
                        placeholder="no-reply@mon-reseau.fr"
                      />
                      <p className="text-xs text-muted-foreground">
                        Poussé dans les réglages du group tenant V2.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="info-sender-name">Nom d'expéditeur</Label>
                      <Input
                        id="info-sender-name"
                        value={infoCard.formData.senderName}
                        onChange={(e) => infoCard.updateField('senderName', e.target.value)}
                        placeholder="Ex: Réseau Paris Est"
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label htmlFor="info-description">Description</Label>
                  <Textarea
                    id="info-description"
                    value={infoCard.formData.description}
                    onChange={(e) => infoCard.updateField('description', e.target.value)}
                    placeholder="Description optionnelle du reseau..."
                    rows={3}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nom</p>
                  <p className="text-foreground">{network.name}</p>
                </div>
                {network.description && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Description
                    </p>
                    <p className="text-foreground">{network.description}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Date de creation
                  </p>
                  <p className="text-foreground">
                    {network.createdAt
                      ? new Date(network.createdAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })
                      : '-'}
                  </p>
                </div>

                {isPhysical && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <Globe className="h-3.5 w-3.5" />
                        Domaine (host)
                      </p>
                      {network.v2Host ? (
                        <a
                          href={`https://${network.v2Host}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-sm text-primary hover:underline inline-flex items-center gap-1"
                        >
                          {network.v2Host}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <p className="text-muted-foreground text-sm">Non défini</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5" />
                        Email d'expédition
                      </p>
                      {network.senderEmail ? (
                        <p className="text-foreground text-sm">
                          {network.senderName ? (
                            <>
                              {network.senderName}{' '}
                              <span className="text-muted-foreground">
                                &lt;{network.senderEmail}&gt;
                              </span>
                            </>
                          ) : (
                            network.senderEmail
                          )}
                        </p>
                      ) : (
                        <p className="text-muted-foreground text-sm">Non défini</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Tenant group V2</p>
                      {network.v2TenantGroupId ? (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-mono text-xs text-muted-foreground break-all">
                            {network.v2TenantGroupId}
                          </span>
                          {getV2StatusBadge(network.v2Status)}
                        </div>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 mt-1">
                          Non initialisé sur la V2
                        </Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        <Tag className="h-3.5 w-3.5" />
                        Marque blanche
                      </p>
                      {network.whiteLabel ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 mt-1">
                          {network.whiteLabel.name}
                        </Badge>
                      ) : (
                        <p className="text-muted-foreground text-sm">Aucune</p>
                      )}
                    </div>
                    {network.whiteLabel && canEdit && (
                      <div className="space-y-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePushBranding}
                          disabled={pushingBranding}
                        >
                          <Upload className="h-3.5 w-3.5" />
                          {pushingBranding ? 'Push en cours...' : 'Pousser le branding'}
                        </Button>
                        {brandingMessage && (
                          <p className="text-xs text-muted-foreground">{brandingMessage}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Écoles du réseau - 2/3 */}
        <Card className="lg:col-span-2">
          <CardHeader
            actions={
              !isPhysical && canEdit ? (
                <EditableCardActions
                  isEditing={schoolsEditing}
                  isSaving={schoolsSaving}
                  onEdit={handleSchoolsStartEditing}
                  onSave={handleSchoolsSave}
                  onCancel={() => {
                    setSchoolsEditing(false);
                    setSchoolsError(null);
                  }}
                />
              ) : isPhysical && canAttach ? (
                <Button size="sm" onClick={() => setAttachOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Ajouter une école
                </Button>
              ) : undefined
            }
          >
            <CardTitle className="text-base flex items-center gap-2">
              <School className="h-4 w-4" />
              Écoles du réseau ({network.schoolCount})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {schoolsError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-700 text-sm mb-4">
                {schoolsError}
              </div>
            )}

            {isPhysical && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
                <Info className="h-3.5 w-3.5" />
                Le rattachement à un réseau physique est définitif : une école ne peut ni être
                retirée ni déplacée.
              </p>
            )}

            {schoolsEditing ? (
              schoolsLoading ? (
                <Skeleton className="h-12" />
              ) : (
                <MultiSelect
                  options={schoolOptions}
                  selected={selectedSchoolValues}
                  onChange={(values) => setSelectedSchoolIds(values.map(Number))}
                  placeholder="Sélectionner des écoles..."
                  searchPlaceholder="Rechercher une école..."
                  emptyMessage="Aucune école trouvée"
                />
              )
            ) : network.schools.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucune école dans ce réseau
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {network.schools.map((school) => (
                  <Link
                    key={school.id}
                    to={`/schools/${school.code}/edit`}
                    className="group"
                  >
                    <Card className="h-full transition-all hover:shadow-md hover:border-primary/50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {/* Logo */}
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted overflow-hidden">
                            {school.logoUrl ? (
                              <img
                                src={school.logoUrl}
                                alt={school.name}
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <School className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate group-hover:text-primary">
                              {school.name}
                            </p>
                            <p className="font-mono text-xs text-muted-foreground">
                              {school.code}
                            </p>
                            {isPhysical && (
                              <p className="font-mono text-xs text-muted-foreground truncate">
                                {school.v2Host || network.v2Host || '-'}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-1 mt-2">
                              {school.hasV1 && (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  <Server className="h-3 w-3 mr-1" />
                                  V1 — {school.v1StatusLabel || school.statusLabel}
                                </Badge>
                              )}
                              {school.hasV2 && (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  <Server className="h-3 w-3 mr-1" />
                                  V2 — {school.v2StatusLabel || '-'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Branding V2 — images de la page de connexion (réseau physique) */}
      {isPhysical && network.whiteLabel && (
        <Card>
          <CardHeader icon={<ImageIcon />} iconColor="pink">
            <CardTitle className="text-base">Branding — page de connexion V2</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Images affichées sur la page de connexion de la marque blanche (tenant group V2).
              Chaque upload est immédiatement poussé dans les réglages du tenant group.
            </p>
            {!network.v2TenantGroupId && (
              <div className="flex items-start gap-2 rounded-lg border bg-amber-50 border-amber-200 p-3 text-sm text-amber-800">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>Tenant group V2 non initialisé : les images ne seront pas poussées tant qu'il n'existe pas.</p>
              </div>
            )}
            {canEdit ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Logo connexion</Label>
                  <FileUpload
                    previewUrl={network.logoSigninUrl || undefined}
                    onChange={() => {}}
                    onUpload={(file) => handleUploadNetworkFile('logoSignin', file)}
                    uploading={uploadingField === 'logoSignin'}
                    label="Logo connexion"
                    description="Page de connexion"
                    maxSize={1 * 1024 * 1024}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Favicon</Label>
                  <FileUpload
                    previewUrl={network.faviconUrl || undefined}
                    onChange={() => {}}
                    onUpload={(file) => handleUploadNetworkFile('favicon', file)}
                    uploading={uploadingField === 'favicon'}
                    label="Favicon"
                    description="Icône navigateur"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Image de couverture</Label>
                  <FileUpload
                    previewUrl={network.coverUrl || undefined}
                    onChange={() => {}}
                    onUpload={(file) => handleUploadNetworkFile('cover', file)}
                    uploading={uploadingField === 'cover'}
                    label="Couverture"
                    description="Fond de la page de connexion"
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { label: 'Logo connexion', url: network.logoSigninUrl },
                  { label: 'Favicon', url: network.faviconUrl },
                  { label: 'Couverture', url: network.coverUrl },
                ].map(({ label, url }) => (
                  <div key={label} className="space-y-2">
                    <Label className="text-sm">{label}</Label>
                    <div className="border bg-muted overflow-hidden flex items-center justify-center h-[160px]">
                      {url ? (
                        <img src={url} alt={label} className="max-h-[140px] w-auto max-w-full object-contain" />
                      ) : (
                        <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {brandingMessage && (
              <p className="text-xs text-muted-foreground">{brandingMessage}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* KPIs par école */}
      {metrics && metrics.schools.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Métriques par école</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th
                      className="text-left py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('name')}
                    >
                      <span className="flex items-center">
                        École
                        <SortIcon field="name" />
                      </span>
                    </th>
                    <th
                      className="text-left py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <span className="flex items-center">
                        Statut
                        <SortIcon field="status" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('studentCount')}
                    >
                      <span className="flex items-center justify-center">
                        Élèves
                        <SortIcon field="studentCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('parentCount')}
                    >
                      <span className="flex items-center justify-center">
                        Parents
                        <SortIcon field="parentCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('teacherCount')}
                    >
                      <span className="flex items-center justify-center">
                        Enseignants
                        <SortIcon field="teacherCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('classCount')}
                    >
                      <span className="flex items-center justify-center">
                        Classes
                        <SortIcon field="classCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('connexionCount')}
                    >
                      <span className="flex items-center justify-center">
                        Connexions
                        <SortIcon field="connexionCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('mailCount')}
                    >
                      <span className="flex items-center justify-center">
                        Mails
                        <SortIcon field="mailCount" />
                      </span>
                    </th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSchools.map((school) => (
                    <tr key={school.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-2 px-3">
                        <div className="flex flex-col">
                          <span className="font-medium">{school.name}</span>
                          <span className="text-xs text-muted-foreground font-mono">{school.code}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant={getStatusBadgeVariant(school.status)}>
                          {school.statusLabel}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.studentCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.parentCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.teacherCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.classCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.connexionCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.mailCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          asChild
                          title="Voir l'école"
                        >
                          <Link to={`/schools/${school.code}/edit`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/50 font-medium">
                    <td className="py-2 px-3" colSpan={2}>Total</td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.studentCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.parentCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.teacherCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.classCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.connexionCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.mailCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attach school dialog (réseaux physiques) */}
      {isPhysical && (
        <AttachSchoolDialog
          open={attachOpen}
          onOpenChange={setAttachOpen}
          networkId={network.id}
          networkName={network.name}
          networkHost={network.v2Host}
          onAttached={() => {
            fetchNetwork();
            fetchMetrics();
          }}
        />
      )}

      {/* Delete network confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le reseau ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer le reseau{' '}
              <strong>{network.name}</strong> ?
              <br />
              <br />
              Les écoles associées ne seront pas supprimées, mais seront retirées du
              réseau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
